/**
 * Section locks — the middle setting between "the user owns this layer" and
 * "the user may not touch it at all".
 *
 * `locked` (the user lock) is all-or-nothing: the layer is off limits. Often
 * that is too blunt — the admin wants the user to TYPE their own name into a
 * heading but not restyle it, or to swap the photo but not drag it off the
 * card. `lockedSections` answers that: the layer stays the user's to edit, and
 * only the named parts of its settings are closed.
 *
 * The keys below are the sections of the right-hand panel, one per block the
 * user would otherwise see. A locked section is not rendered for a user at all
 * rather than shown greyed out — a fill-in panel full of dead controls is worse
 * than a shorter one — and the two geometry sections are enforced on the canvas
 * too, or "Position: locked" would be undone by the first drag.
 */
import { ELEMENT_TYPES } from './documentSchema';

const { TEXT, PLACEHOLDER, IMAGE, SHAPE, QRCODE, BARCODE, TABLE } = ELEMENT_TYPES;
const TEXTY = [TEXT, PLACEHOLDER];

/**
 * `types: null` means every element type has this section.
 * Order here is the order the admin sees, and it follows the panel itself.
 *
 * Only sections a USER can actually see belong here. The admin-only ones —
 * "Linked text" (which wires layers together) and "Placeholder" (which names the
 * data key) — render null outside admin mode, so a lock on them would control
 * nothing and would tell the admin it controlled something.
 */
export const LOCKABLE_SECTIONS = [
    // The Position PANEL is admin-only, so for a user this lock is entirely about
    // the canvas: it is what stops them dragging the layer out of place.
    { key: 'position', label: 'Position', types: null, hint: 'Stops the user dragging or rotating this layer' },
    { key: 'layout', label: 'Layout', types: null, hint: 'Width and height — also removes the resize handles' },
    { key: 'appearance', label: 'Appearance', types: null, hint: 'Opacity, corner radius, blend mode' },
    // The WORDS, not the admin-only "Placeholder" block: locking this stops the
    // user retyping the layer, on the canvas and in their fill-in list alike.
    { key: 'content', label: 'Text content', types: TEXTY, hint: 'Stops the user changing the words in this layer' },
    { key: 'textPath', label: 'Text Path', types: TEXTY },
    { key: 'typography', label: 'Typography', types: [...TEXTY, TABLE] },
    { key: 'textFill', label: 'Text colour', types: [...TEXTY, TABLE] },
    { key: 'fill', label: 'Fill / background', types: [...TEXTY, IMAGE, SHAPE] },
    { key: 'border', label: 'Border', types: [...TEXTY, IMAGE, SHAPE, QRCODE, BARCODE] },
    { key: 'stroke', label: 'Stroke', types: TEXTY },
    { key: 'image', label: 'Image', types: [IMAGE], hint: 'Replacing the picture, fit and crop' },
    { key: 'shape', label: 'Shape', types: [SHAPE] },
    { key: 'qr', label: 'QR code', types: [QRCODE] },
    { key: 'barcode', label: 'Barcode', types: [BARCODE] },
    { key: 'table', label: 'Table', types: [TABLE] },
    { key: 'effects', label: 'Effects', types: null },
];

/** The sections that actually appear for one element — nothing else is offered. */
export function sectionsFor(el) {
    if (!el) return [];
    return LOCKABLE_SECTIONS.filter((s) => !s.types || s.types.includes(el.type));
}

/**
 * Is this part of the panel closed to whoever is looking?
 *
 * Only ever true for a USER: the admin sets these locks and must be able to see
 * past them, exactly as with the user lock (see `isLockedFor`).
 */
export function isSectionLocked(el, key, mode) {
    if (mode !== 'user' || !el) return false;
    const list = el.lockedSections;
    return Array.isArray(list) && list.includes(key);
}

/** `lockedSections` with one key added or removed — always a fresh array. */
export function toggleSectionLock(el, key) {
    const list = Array.isArray(el?.lockedSections) ? el.lockedSections : [];
    return list.includes(key) ? list.filter((k) => k !== key) : [...list, key];
}
